import {
  Button,
  IconButton,
  Popover,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Box } from '@mui/system';
import { useState } from 'react';
import { usePartyManager } from './PartyManagerContext';

import MemoryOutlinedIcon from '@mui/icons-material/MemoryOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import { useInterfaceStore } from '@/store/interface';
import proxy from '@/services/ProxyService';
import { LoadingButton } from '@mui/lab';
import { toast } from 'react-toastify';
import ToastMessage from '@/components/toasts/ToastMessage';

export const PartyHeader = () => {
  const [memorySearchAnchorEl, setMemorySearchAnchorEl] = useState(null);
  const [memorySearchTerm, setMemorySearchTerm] = useState('');
  const [loading, setLoading] = useState(false);

  const INTERFACE_STORE = useInterfaceStore.getState();

  const {
    partyData,
    setPartyData,
    currentCodemetaParty,
    setIsPartyManagerLoading,
  } = usePartyManager();

  const handleMemorySearchClick = (event) => {
    setMemorySearchAnchorEl(event.currentTarget);
  };

  const handleMemorySearchClose = () => {
    setMemorySearchAnchorEl(null);
    setMemorySearchTerm('');
  };

  const handleMemorySearchExecute = async () => {
    if (!memorySearchTerm) return;
    if (!INTERFACE_STORE.llm) return;

    setIsPartyManagerLoading(true);
    setLoading(true);

    const { data: res } = await proxy
      .post(`/ai/${INTERFACE_STORE.llm}`, {
        prompt: `Please try to generate a JSON of the following structure from the given data:
      {
        "firstName": "", // Only for type PERSON
        "lastName": "", // Only for type PERSON
        "company": "",
        "partyType": "PERSON" | "COMPANY",
        "addresses":[{
          "street1": "",
          "zipcode": "",
          "city": "",
          "countryCode": "" // Try to guess based on city or domain ending
        }],
        "email": "",
        "phone": "",
        "website": "",
        "vatId": "",
      }
      Only output the JSON without any additional text.
      The data to analyze is: ${memorySearchTerm}`,
      })
      .catch((error) => {
        console.error('Error during AI request:', error);
        return { data: {} };
      });

    if (res?.completion) {
      try {
        const data = JSON.parse(res.completion);
        setPartyData((prevData) => {
          const newData = { ...prevData };
          Object.keys(data).forEach((key) => {
            const value = data[key];
            if (newData[key]?.edit !== undefined) {
              newData[key].edit = value;
              newData[key].selected = 'edit';
            } else if (key === 'addresses') {
              newData[key] = [
                {
                  street1: {
                    edit: value[0].street1 || '',
                    selected: 'edit',
                  },
                  zipcode: {
                    edit: value[0].zipcode || '',
                    selected: 'edit',
                  },
                  city: {
                    edit: value[0].city || '',
                    selected: 'edit',
                  },
                  countryCode: {
                    edit: value[0].countryCode || '',
                    selected: 'edit',
                  },
                },
              ];
            }
          });
          return newData;
        });
      } catch (error) {
        console.error('Error parsing AI response:', error);
        toast.error(
          <ToastMessage
            title="Fehler bei der Abfrage"
            subtitle="Die Antwort der KI konnte nicht verarbeitet werden."
          />,
        );
        return;
      } finally {
        handleMemorySearchClose();
        setIsPartyManagerLoading(false);
        setLoading(false);
      }
    }
  };

  const isMemorySearchOpen = Boolean(memorySearchAnchorEl);
  const memorySearchId = isMemorySearchOpen
    ? 'memory-search-popover'
    : undefined;
  const isPerson = partyData.partyType === 'PERSON';
  const isNewParty = !currentCodemetaParty?._id;

  const getSuffix = () => {
    const firstName = partyData.firstName[partyData.firstName.selected] || '';
    const lastName = partyData.lastName[partyData.lastName.selected] || '';
    const company = partyData.company[partyData.company.selected] || '';
    const customerNumber =
      partyData.customerNumber[partyData.customerNumber.selected] || null;
    const supplierNumber =
      partyData.supplierNumber[partyData.supplierNumber.selected] || null;

    if (isNewParty && isPerson) return `${isNewParty ? 'Neue' : ''} Kontakt`;
    if (isNewParty && !isPerson) return `${isNewParty ? 'Neue' : ''} Firma`;
    if (isPerson) return `Kontakt: ${lastName}, ${firstName}`;
    if (customerNumber && supplierNumber) {
      return `Kunde/Lieferant ${customerNumber}: ${company}`;
    }
    if (customerNumber) {
      return `Kunde ${customerNumber}: ${company}`;
    }
    if (supplierNumber) {
      return `Lieferant ${supplierNumber}: ${company}`;
    }
    return `Firma: ${company}`;
  };

  const suffix = getSuffix();

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
          {suffix}
        </Typography>
        {isNewParty && (
          <Tooltip title="Suche mit KI nach Informationen">
            <IconButton
              aria-label="master-search"
              onClick={handleMemorySearchClick}
              aria-describedby={memorySearchId}
            >
              <MemoryOutlinedIcon />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      <Popover
        id={memorySearchId}
        open={isMemorySearchOpen}
        anchorEl={memorySearchAnchorEl}
        onClose={handleMemorySearchClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Box
          sx={{
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            width: 300,
          }}
        >
          <TextField
            label="Suche mit KI"
            placeholder="Geben Sie Suchebegriffe ein..."
            variant="outlined"
            size="small"
            value={memorySearchTerm}
            onChange={(e) => setMemorySearchTerm(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleMemorySearchExecute();
              }
            }}
            autoFocus
            multiline
            rows={3}
          />
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button
              onClick={handleMemorySearchClose}
              size="small"
              color="error"
            >
              Abbrechen
            </Button>
            <LoadingButton
              variant="contained"
              onClick={handleMemorySearchExecute}
              size="small"
              startIcon={<SearchOutlinedIcon />}
              loading={loading}
            >
              Suchen
            </LoadingButton>
          </Box>
        </Box>
      </Popover>
    </>
  );
};
